import { benchmark } from 'shared/containers/App/saga';
import { call, put, select, takeLatest, CallEffect, SelectEffect, PutEffect } from 'redux-saga/effects';
import { Action, PayloadAction } from '@reduxjs/toolkit';
import { scheduleInstallationActions } from '@buy-viasat/redux/src/schedule-installation';
import {
  selectCoordinates,
  selectServiceAddressValues,
  selectUnsanitizedAddressLines,
} from '@buy-viasat/redux/src/serviceability';
import clientLogger from '../../utils/clientLogger';
import getInstallationDatesAsync, {
  GetInstallationDatesPayload,
} from '../../providers/requests/fulfillment/getInstallationDates';
import { parseTimeSlotsByDay } from './utils';
import { AvailableInstallationDates } from '@buy-viasat/redux/src/schedule-installation';
import { selectCartId } from '@buy-viasat/redux/src/cart';
import {
  selectFulfillmentAgreementId,
  selectSalesAgreementId,
  selectUrlParams,
  QueryParams,
} from '@buy-viasat/redux/src/app';
import { AddressType } from '@buy-viasat/redux/src/address';
import { sanitizeAddressLines } from '@buy-viasat/redux/src/plan';
import { selectNoAvailableInstallationDates } from '@buy-viasat/redux/src/schedule-installation';
import { Coordinates, ScheduleR0 } from '@buy-viasat/types/build/bv';
import { adobeTargetDataLayer } from 'shared/containers/Analytics';
import { DataLayerEvents } from 'shared/containers/Analytics/types';
import { USE_NEW_API } from 'shared/utils/dataFetchingHooks/constants';
import getAppointments, { AvailableAppointments } from 'shared/providers/requests/fulfillment/getAppointments';

export function toIsoDateTime(value?: string): string {
  const parsed = value ? new Date(value) : new Date();
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
}

export function addDays(dateTimeIso: string, days: number): string {
  const parsed = new Date(dateTimeIso);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  parsed.setUTCDate(parsed.getUTCDate() + days);
  return parsed.toISOString();
}

export function* getInstallationDatesSaga(
  action: PayloadAction<string>,
): Generator<
  CallEffect | SelectEffect | PutEffect,
  void,
  GetInstallationDatesPayload & AddressType & string & QueryParams & AvailableAppointments & Coordinates & string[]
> {
  const serviceAddress = yield select(selectServiceAddressValues);
  const coordinates = yield select(selectCoordinates);
  const cartId = yield select(selectCartId);
  const { dealerId, partnerId } = yield select(selectUrlParams);
  const salesAgreementId = yield select(selectSalesAgreementId);
  const fulfillmentAgreementId = yield select(selectFulfillmentAgreementId);
  const unsanitizedAddressLines: string[] | null = yield select(selectUnsanitizedAddressLines);
  // Dispatch an action to indicate that the installation dates are loading
  yield put(scheduleInstallationActions.setInstallationDatesLoading());

  try {
    let instalationDates;
    const fromDate = toIsoDateTime(action.payload);
    const toDate = addDays(fromDate, 30);

    if (USE_NEW_API) {
      const data: AvailableAppointments = yield call(getAppointments, {
        from: fromDate,
        to: toDate,
        addressLines: serviceAddress.addressLines[0],
        municipality: serviceAddress.municipality,
        region: serviceAddress.region,
        countryCode: serviceAddress.countryCode,
        postalCode: serviceAddress.postalCode,
        latitude: coordinates.latitude + '',
        longitude: coordinates.longitude + '',
        fulfillmentAgreementId: fulfillmentAgreementId,
        shoppingCartId: cartId,
      });
      instalationDates = data;
    } else {
      const { data } = yield call(getInstallationDatesAsync, {
        cartId,
        location: sanitizeAddressLines({ address: serviceAddress, coordinates }, unsanitizedAddressLines),
        from: action.payload,
        dealerId,
        partnerId,
        salesAgreementId,
        fulfillmentAgreementId,
      });
      instalationDates = data?.getInstallationDates || data;
    }

    if (instalationDates?.schedules) {
      const installationDates: AvailableInstallationDates = parseTimeSlotsByDay(
        instalationDates.schedules,
        instalationDates?.timezone ?? '',
      );

      const filteredPalData = (instalationDates.schedules as Array<ScheduleR0 | null | undefined>).filter(
        Boolean,
      ) as ScheduleR0[];

      yield put(scheduleInstallationActions.setPALInstallationDates(filteredPalData)); // Sets PAL data later used to construct work order
      yield put(scheduleInstallationActions.setAvailableInstallationDates(installationDates)); // Sets installation dates displayed in the UI

      const noAvailableInstallationDates = yield select(selectNoAvailableInstallationDates);

      adobeTargetDataLayer.push({
        event: DataLayerEvents.AA_EVENT,
        eventData: {
          event: 'noAvailableInstallationDates',
          scheduleInstallation: {
            noAvailableInstallationDates: noAvailableInstallationDates,
          },
        },
      });
    }
  } catch (e) {
    clientLogger('Error in loadInstallation saga', e);
    yield put(scheduleInstallationActions.getInstallationDatesError());
  } finally {
    // Dispatch an action to indicate that the installation dates loading has completed
    yield put(scheduleInstallationActions.installationDatesLoadingComplete());
  }
}

function handleLowerEnvLogging(action: Action | PayloadAction<string>) {
  if (process.env['NODE_ENV'] !== 'production') {
    console.log(action);
  }
}

export function* scheduleInstallationSaga(): Generator {
  // yield takeEvery('*', handleLowerEnvLogging); // Uncomment to enable logging each action dispatch, for debugging purposes
  yield takeLatest(
    scheduleInstallationActions.getInstallationDates.type,
    benchmark<string, CallEffect | SelectEffect | PutEffect, void, GetInstallationDatesPayload & AddressType & string>(
      getInstallationDatesSaga,
    ),
  );
}
